<template>
  <c-box>
    <c-box position="relative">
      <c-input
        id="searchTerm"
        position="relative"
        mb="0.75rem"
        pl="3rem"
        :is-full-width="true"
        border-radius="input"
        border-width="1px"
        background="transparent"
        v-model.trim="searchTerm"
        autocomplete="off"
        height="12"
        :placeholder="placeholder || $t('productSearch.placeholder')"
        @keydown.up.prevent.stop="selectPrevByKeyboard()"
        @keydown.down.prevent.stop="selectNextByKeyboard()"
        @keydown.enter="enterPressed()"
        @keydown.esc="clearSearch()"
        @blur="hideResults()"
        @focus="
          showResults();
          $event.target.select();
          $emit('focus', $event);
        "
        :_hover="{ borderColor: 'brand.purple' }"
        :_focus="{ borderColor: 'brand.purple' }"
        :_active="{ borderColor: 'brand.purple' }"
        v-chakra="{
          '&::placeholder': {
            color: 'brand.purple',
          },
        }"
        data-cy="quick-product-return-search-input"
      />
      <c-icon
        name="search"
        position="absolute"
        left="0.75rem"
        top="50%"
        transform="translateY(-50%)"
        size="1.5rem"
        color="brand.grayDark"
      />
    </c-box>
    <slot name="after-input"></slot>
    <c-box
      v-if="loading"
      class="results-wrapper"
      position="absolute"
      width="80%"
      max-width="500px"
      z-index="10"
    >
      <c-flex
        justify-content="center"
        border-radius="input"
        background="white"
        box-shadow="base"
        py="md"
        border-color="brand.gray"
        v-chakra="
          (theme) => ({
            borderWidth: theme.border.base,
          })
        "
      >
        <Spinner />
      </c-flex>
    </c-box>
    <c-box
      v-else-if="resultsVisible && searchResults"
      class="results-wrapper"
      position="absolute"
      width="80%"
      max-width="500px"
    >
      <c-box
        data-cy="quick-product-search-results"
        border-radius="input"
        background="white"
        box-shadow="base"
        position="relative"
        z-index="10"
        border-color="brand.gray"
        v-chakra="
          (theme) => ({
            borderWidth: theme.border.base,
          })
        "
      >
        <!-- Tried to add border-bottom-width with v-chakra callback from theme,
        but it didn't work when selectedIndex was active and the whole border
        went missing -> had to hardcode the value to make it work... -->
        <c-box
          v-for="(product, index) in searchResults.value"
          :key="product.sku"
          @click.prevent.stop="pickProduct(product)"
          cursor="pointer"
          font-weight="300"
          py="md"
          :px="index === selectedIndex && 'xl'"
          :mx="index === selectedIndex ? '0' : 'xl'"
          :background="index === selectedIndex && 'brand.gray'"
          :color="index === selectedIndex && 'brand.purpleDark'"
          overflow="auto"
          line-height="1.1"
          :_hover="{
            background: 'brand.gray',
            color: 'brand.purpleDark',
            mx: '0',
            px: 'xl',
          }"
          border-bottom-color="gray.200"
          border-bottom-width="1px"
        >
          <c-text as="span" float="left" width="70%" pr="sm">{{
            `${product.product_name}${product.subtitle ? ` - ${product.subtitle}` : ""}`
          }}</c-text>
          <c-text as="span" float="right" width="30%" color="gray.600">{{ product.sku }}</c-text>
        </c-box>
        <c-box
          v-if="searchResults.value.length < searchResults.count"
          color="brand.grayDark"
          font-weight="300"
          py="sm"
          px="xl"
          overflow="auto"
        >
          {{ $t("quickSearch.displayingFirstOnes") }}
        </c-box>
        <c-box
          v-if="searchResults.value.length === 0"
          color="brand.grayDark"
          font-weight="300"
          py="sm"
          px="xl"
          overflow="auto"
        >
          {{ $t("quickSearch.noResults") }}
        </c-box>
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import Spinner from "@/components/Spinner.vue";
import { CBox, CFlex, CIcon, CInput, CText } from "@chakra-ui/vue";
import { IPartnerProductSearchResult } from "@iloq/shared";
import _ from "lodash";
import { defineComponent } from "vue";

export default defineComponent({
  name: "QuickProductReturnSearch",
  components: {
    Spinner,
    CBox,
    CInput,
    CFlex,
    CText,
    CIcon,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    setPickedProductToField: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    includeArchived: {
      type: Boolean,
      default: undefined,
    },
  },
  data() {
    return {
      searchTerm: "",
      lastSearchTerm: "",
      typing: false,
      loading: false,
      searchResults: null as IPartnerProductSearchResult | null,
      selectedIndex: -1,
      resultsVisible: true,
      externalChangeOngoing: false,
    };
  },
  watch: {
    value(newValue) {
      if (this.searchTerm !== newValue) {
        this.externalChangeOngoing = true;
        this.searchTerm = newValue;
      }
    },
    searchTerm(newTerm) {
      if (this.externalChangeOngoing) {
        this.externalChangeOngoing = false;
        return;
      }

      this.$emit("update:value", newTerm);
      this.$emit("field-change");
      if (newTerm.trim() !== (this.lastSearchTerm || "").trim()) {
        this.typing = true;
        this.resultsVisible = true;
        this.searchResults = null;
        this.search(newTerm);
      }
    },
  },
  methods: {
    search: _.debounce(function (this: any, term: string) {
      if (term !== this.lastSearchTerm) {
        const searchAction = this.includeArchived
          ? "productSearch/searchArchivedProductsQuick"
          : "productSearch/searchProductsQuick";
        this.lastSearchTerm = term;

        if (term.trim().length > 0) {
          this.loading = true;
          this.$store
            .dispatch(searchAction, { term, top: 15 })
            .then((results) => {
              if (term === this.lastSearchTerm) {
                this.searchResults = results;
                this.selectedIndex = -1;
              }
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.searchResults = null;
        }

        this.typing = false;
      }
    }, 400),
    pickProduct(p) {
      if (this.setPickedProductToField) {
        this.searchTerm = `${p.sku} ${p.product_name}${p.subtitle ? ` - ${p.subtitle}` : ""}`;

        // Disable search & result list until input text gets changed
        this.lastSearchTerm = this.searchTerm;
        this.searchResults = null;

        this.$emit("update:searchTerm", this.searchTerm);
        this.$emit("field-change");
      }
      this.$emit("product-picked", p);
    },
    selectPrevByKeyboard() {
      if (this.searchResults) {
        this.selectedIndex -= 1;
        if (this.selectedIndex < 0) {
          this.selectedIndex = this.searchResults.value.length - 1;
        }
      }
    },
    selectNextByKeyboard() {
      if (this.searchResults) {
        this.selectedIndex += 1;
        if (this.selectedIndex >= this.searchResults.value.length) {
          this.selectedIndex = 0;
        }
      }
    },
    enterPressed() {
      if (this.resultsVisible) {
        this.pickProductByKeyboard();
      } else {
        this.resultsVisible = true;
      }
    },
    pickProductByKeyboard() {
      if (this.selectedIndex >= 0 && this.searchResults) {
        this.pickProduct(this.searchResults.value[this.selectedIndex]);
      }
    },
    hideResults() {
      setTimeout(() => {
        this.resultsVisible = false;
        this.$emit("field-blur");
      }, 300);
    },
    showResults() {
      this.resultsVisible = true;
    },
    clearSearch() {
      if (!this.setPickedProductToField) {
        this.searchTerm = "";
        this.$emit("update:value", this.searchTerm);
        this.$emit("field-change");
      } else {
        this.resultsVisible = false;
      }
    },
  },
});
</script>
